import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/cn'

/**
 * An interactive chart or tool inside MDX, served as its own page from
 * `public/embeds/` and framed here.
 *
 * The framed page reports its height with
 * `postMessage({ type: 'sg-embed-height', height })` whenever it resizes, so
 * the frame grows to fit instead of scrolling. Only same-origin messages from
 * this frame are read. `initialHeight` holds space until the first report.
 */
export function InteractiveEmbed({
  src,
  title,
  initialHeight = 480,
  className,
}: {
  src: string
  title: string
  initialHeight?: number
  className?: string
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(initialHeight)

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.source !== frame.current?.contentWindow) return
      const data = event.data as { type?: string; height?: unknown } | null
      if (data?.type === 'sg-embed-height' && typeof data.height === 'number') {
        setHeight(Math.ceil(data.height))
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return (
    <iframe
      ref={frame}
      src={src}
      title={title}
      loading="lazy"
      height={height}
      className={cn('my-8 w-full border-0', className)}
    />
  )
}
